import { SearchOutlined } from '@ant-design/icons'
import { Alert, Button, Input, Select, Table, message } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import CreditLimitModal from './CreditLimitModal'
import { listCreditMembers, teamErrorMessage } from './teamApi'
import { usagePercent } from './teamCreditsUsage'
import type { AllowanceState, MemberAllowance, MemberStatus } from './teamTypes'
import { TeamAvatar, useTeamFormat } from './teamUi'

const PAGE_SIZE = 10

const STATE_CLASS: Record<AllowanceState, string> = {
  ok: 'tm-tag-on',
  unlimited: '',
  exhausted: 'tm-tag-warn',
  disabled: 'tm-tag-off',
}

/**
 * VistoeraTASK-00529 - 成员额度 table: member, role, month-to-date use (ledger
 * aggregation), Monthly Spending Limit (config), remaining, state. Server-side
 * search / filter / pagination. Editing is shown only where the backend would
 * allow it (`can_set_limit`) and is authorised again server-side.
 */
export default function MemberAllowanceTable({
  workspaceId,
  onChanged,
}: {
  workspaceId: string
  onChanged: () => void
}) {
  const { t } = useTranslation('team')
  const { num } = useTeamFormat()
  const [items, setItems] = useState<MemberAllowance[]>([])
  const [total, setTotal] = useState(0)
  const [teamAvailable, setTeamAvailable] = useState<string | null>(null)
  const [page, setPage] = useState(1)
  const [q, setQ] = useState('')
  const [debouncedQ, setDebouncedQ] = useState('')
  const [status, setStatus] = useState<MemberStatus | undefined>()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [editing, setEditing] = useState<MemberAllowance | null>(null)
  const reqSeq = useRef(0)

  useEffect(() => {
    const id = window.setTimeout(() => {
      setDebouncedQ(q.trim())
      setPage(1)
    }, 300)
    return () => window.clearTimeout(id)
  }, [q])

  const load = useCallback(async () => {
    const seq = ++reqSeq.current
    setLoading(true)
    setError(null)
    try {
      const res = await listCreditMembers(workspaceId, {
        q: debouncedQ || undefined,
        status,
        page,
        page_size: PAGE_SIZE,
      })
      if (seq !== reqSeq.current) return
      setItems(res.data.items)
      setTotal(res.data.total)
      setTeamAvailable(res.data.team_available_credits ?? null)
    } catch (e) {
      if (seq !== reqSeq.current) return
      setError(teamErrorMessage(e, t('credits.members.loadFailed')))
    } finally {
      if (seq === reqSeq.current) setLoading(false)
    }
  }, [workspaceId, debouncedQ, status, page, t])

  useEffect(() => {
    void load()
  }, [load])

  const columns: ColumnsType<MemberAllowance> = [
    {
      title: t('credits.members.col.member'),
      key: 'member',
      render: (_: unknown, r) => (
        <div className="tm-member-cell">
          <TeamAvatar name={r.display_name} size={34} round />
          <div className="tm-member-text">
            <div className="tm-member-name">
              {r.display_name}
              {r.is_self && <span className="tm-self">{t('members.you')}</span>}
            </div>
            <div className="tm-hint">{r.email}</div>
          </div>
        </div>
      ),
    },
    {
      title: t('credits.members.col.role'),
      key: 'role',
      width: 120,
      render: (_: unknown, r) => <span className={`tm-tag tm-tag-${r.role_key}`}>{t(`role.${r.role_key}`)}</span>,
    },
    {
      title: t('credits.members.col.used'),
      key: 'used',
      width: 120,
      align: 'right',
      render: (_: unknown, r) => <strong>{num(r.month_used)}</strong>,
    },
    {
      title: t('credits.members.col.limit'),
      key: 'limit',
      width: 130,
      align: 'right',
      render: (_: unknown, r) =>
        r.monthly_limit === null ? <span className="tm-hint">{t('credits.unlimited')}</span> : num(r.monthly_limit),
    },
    {
      title: t('credits.members.col.remaining'),
      key: 'remaining',
      width: 170,
      render: (_: unknown, r) => {
        if (r.remaining === null) return <span className="tm-hint">{t('credits.unlimited')}</span>
        const pct = usagePercent(r.month_used, r.monthly_limit)
        return (
          <div className="tm-remaining">
            <span>{num(r.remaining)}</span>
            {pct !== null && (
              <div className="tm-hbar-track" aria-hidden="true">
                <div className={`tm-hbar-fill${pct >= 100 ? ' is-full' : ''}`} style={{ width: `${pct}%` }} />
              </div>
            )}
          </div>
        )
      },
    },
    {
      title: t('credits.members.col.state'),
      key: 'state',
      width: 120,
      render: (_: unknown, r) => <span className={`tm-tag ${STATE_CLASS[r.state]}`}>{t(`credits.members.state.${r.state}`)}</span>,
    },
    {
      title: t('credits.members.col.actions'),
      key: 'actions',
      width: 110,
      render: (_: unknown, r) =>
        r.can_set_limit ? (
          <Button type="link" size="small" onClick={() => setEditing(r)} data-testid={`team-limit-edit-${r.user_id}`}>
            {t('credits.members.setLimit')}
          </Button>
        ) : (
          <span className="tm-hint" title={r.role_key === 'owner' ? t('credits.members.ownerHint') : undefined}>
            —
          </span>
        ),
    },
  ]

  return (
    <section className="tm-card" data-testid="team-allowance-table">
      <div className="tm-card-head">
        <span className="tm-card-title">{t('credits.members.title')}</span>
      </div>
      <div className="tm-toolbar">
        <Input
          allowClear
          prefix={<SearchOutlined />}
          placeholder={t('members.search')}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          style={{ maxWidth: 280 }}
        />
        <Select<MemberStatus>
          allowClear
          placeholder={t('members.filterStatus')}
          value={status}
          onChange={(v) => {
            setStatus(v)
            setPage(1)
          }}
          style={{ width: 140 }}
          options={[
            { value: 'active', label: t('status.active') },
            { value: 'disabled', label: t('status.disabled') },
          ]}
        />
      </div>
      {error && (
        <Alert
          type="error"
          showIcon
          title={error}
          style={{ marginBottom: 12 }}
          action={
            <Button size="small" onClick={() => void load()}>
              {t('common.retry')}
            </Button>
          }
        />
      )}
      <Table<MemberAllowance>
        rowKey="user_id"
        size="middle"
        loading={loading}
        columns={columns}
        dataSource={items}
        locale={{ emptyText: t('credits.members.empty') }}
        pagination={{ current: page, pageSize: PAGE_SIZE, total, showSizeChanger: false, hideOnSinglePage: true, onChange: setPage }}
        scroll={{ x: 880 }}
      />
      <div className="tm-hint" style={{ marginTop: 8 }}>
        {t('credits.members.footnote')}
      </div>
      <CreditLimitModal
        workspaceId={workspaceId}
        member={editing}
        teamAvailable={teamAvailable}
        onClose={() => setEditing(null)}
        onSaved={(row) => {
          setEditing(null)
          void message.success(t('credits.limit.saved', { name: row.display_name }))
          void load()
          onChanged()
        }}
      />
    </section>
  )
}
